import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kNativeDialog, IkNativeDialogOptions } from "../../../Admin/ReUsableTS/kNativeDialog";
import { AppContact } from "./AppIndex";
import { AppChatArchiveWho, AppChatSettingValue } from "./AppChatArchiveRules";
import { AppChatSettingRow, AppChatSettingsModel, AppChatSettingsRules } from "./AppChatSettingsRules";
import { AppChatSettingsPort, AppChatSettingsRun, IAppChatSettingsScreen } from "./AppChatSettingsRun";
import { AppChatSettingsView } from "./AppChatSettingsView";
import { AppLiveWindowClock } from "./AppLiveFeed";
import { AppTranslateChip, AppTrDomScreen, IAppTrChipHost } from "./AppTranslateChip";
import { AppTranslateView } from "./AppTranslateView";
import { AppTranslator } from "./AppTranslator";
import { AppFocus, IFocusEl } from "./AppFocus";
import { AppLiveText } from "./AppLiveText";

// AppChatSettingsDialog - THE "CHAT SETTINGS" DIALOG IN THE BROWSER (owner, 2026-10-13): a native modal `<dialog>`
// (the shared `kNativeDialog`: Esc, the backdrop, the focus trap and the focus's way back are the browser's), opened
// from the chat header's "⋯" menu, from the header's translation chip, and from the "⋯" beside each chat in the list.
// It joins three things for ONE chat: `AppChatSettingsRun` (archive, notifications, disappearing messages, the star),
// an `AppTranslateChip` in editor form (the translation settings) and this file's `AppChatSettingsScreen` (the DOM).
//
// ⛔ NOTHING IS REBUILT WHILE IT IS OPEN: the settings rows are made once (`AppChatSettingsView.Body`) and `Paint` only
//    changes words and attributes that differ - a control replaced under the pointer gets no click (the 2026-10-12
//    audit's P0), and a live region rewritten with the same words is read out again.
// ⛔ A CONTROL THAT CANNOT BE USED IS `aria-disabled`, NOT `disabled`: it stays reachable by keyboard, so its reason
//    (the service's sentence: tooltip, description and the line under the row) can be read. A press on it does nothing.
// ⛔ NO PROPERTY IS EVER SHOWN WITHOUT A VALUE (`AppChatSettingsRules`' header) and nothing here can send a message.

/** What the page does for the dialog. */
export interface IAppChatSettingsHost {
    /** Fresh credentials for this chat (asked before every request). */
    Who(Selected: AppContact): AppChatArchiveWho;
    /** The visit's translator when translation can be offered for this chat at all; else null (no section). */
    TranslatorFor(Selected: AppContact): AppTranslator | null;
    /** A setting change was confirmed (the Starred / Archived list views read again). */
    SettingChanged(Selected: AppContact, Setting: string): void;
    /** This chat's translation settings were saved. */
    TranslationChanged(Href: string, TurnedOn: boolean): void;
    /** Where the keyboard goes back to when what opened the dialog is no longer on the page. */
    Opener(Selected: AppContact): HTMLElement | null;
}

/** The dialog's settings rows, changed in place. */
export class AppChatSettingsScreen implements IAppChatSettingsScreen {
    private Root: ParentNode;

    public constructor(Root: ParentNode) {
        this.Root = Root;
    }

    public Paint = (Model: AppChatSettingsModel): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Failed: boolean = Model.Phase === AppChatSettingsModel.Failed;
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.FailId), Failed === false);
            AppChatSettingsScreen.Text(this.El(AppChatSettingsView.FailWhyId), Model.Fail);
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.RetryId), Model.CanRetry === false);
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.WaId), Failed);
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.LocalId), Failed);
            for (const Row of Model.Rows) { this.PaintRow(Row, Model.Elapsed); }
            this.PaintInfo(Model, Failed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.Paint");
        }
    };

    /** "About this contact" and the lock line: a field shows only once it has a value; written only where it differs. */
    private PaintInfo = (Model: AppChatSettingsModel, Failed: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.LockId), Failed || Model.Locked === false);
            const Shown: Set<string> = new Set<string>();
            for (const Field of Model.Info) {
                Shown.add(Field[0]);
                AppChatSettingsScreen.Text(this.El(AppChatSettingsView.Id("info", Field[0] + "-l")), Field[1]);
                AppChatSettingsScreen.Text(this.El(AppChatSettingsView.Id("info", Field[0] + "-v")), Field[2]);
            }
            for (const Field of AppChatSettingsRules.InfoFields) { AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.Id("info", Field)), Shown.has(Field) === false); }
            AppChatSettingsScreen.Text(this.El(AppChatSettingsView.InfoNoteId), Model.InfoNote);
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.InfoNoteId), Model.InfoNote.length === 0);
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.InfoId), Failed || Model.Info.length === 0 && Model.InfoNote.length === 0);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.PaintInfo");
        }
    };

    /** An action's confirm step opened (`Open`) or closed; the keyboard goes to Cancel / back to the first button. */
    public Confirm = (Setting: string, Open: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Ask: HTMLElement | null = this.El(AppChatSettingsView.Id(Setting, "ask"));
            AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.Id(Setting, "confirm")), Open === false);
            AppChatSettingsScreen.Attr(Ask, "aria-expanded", String(Open));
            let Next: HTMLElement | null = Ask; if (Open === true) { Next = this.El(AppChatSettingsView.Id(Setting, "cancel")); }
            if (Next !== null && typeof Next.focus === "function") { Next.focus(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.Confirm");
        }
    };

    private El = (Id: string): HTMLElement | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            return this.Root.querySelector("[id=\"" + Id + "\"]");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.El");
            return null;
        }
    };

    private PaintRow = (Row: AppChatSettingRow, Elapsed: number): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Box: HTMLElement | null = this.Root.querySelector("[" + AppChatSettingsView.RowAttr + "=\"" + Row.Setting + "\"]");
            AppChatSettingsScreen.Attr(Box, "aria-busy", String(Row.Loading === true || Row.Busy === true));
            AppChatSettingsScreen.Hide(this.Part(Row, "skel"), Row.Loading === false);
            AppChatSettingsScreen.Text(this.Part(Row, "words"), Row.Words);
            // Owner review 2026-11-02: one line per setting - the control shows the value, so the words are not drawn
            // beside it (only an action row, which has no state control, keeps them); they ride the control's tooltip.
            AppChatSettingsScreen.Hide(this.Part(Row, "words"), Row.Loading === true || AppChatSettingsRules.IsAction(Row.Setting) === false);
            AppChatSettingsScreen.Attr(this.Part(Row, "words"), "title", Row.Source);
            AppChatSettingsScreen.Text(this.Part(Row, "hint"), Row.Hint);
            AppChatSettingsScreen.Hide(this.Part(Row, "hint"), Row.Hint.length === 0);
            let Secs: string = "";
            if (Elapsed >= 2 && (Row.Loading === true || Row.Busy === true)) { Secs = String(Elapsed) + " s"; }
            AppChatSettingsScreen.Text(this.Part(Row, "secs"), Secs);
            AppChatSettingsScreen.Text(this.Part(Row, "why"), Row.Why);
            AppChatSettingsScreen.Hide(this.Part(Row, "why"), Row.Why.length === 0);
            AppChatSettingsScreen.Text(this.Part(Row, "note"), Row.Note);
            AppChatSettingsScreen.Hide(this.Part(Row, "note"), Row.Note.length === 0);
            AppChatSettingsScreen.Attr(this.Part(Row, "ctl"), "title", Row.Why.length > 0 ? Row.Why : (Row.Loading === true ? "" : Row.Words));
            AppChatSettingsScreen.Attr(this.Part(Row, "info"), "title", Row.Why);
            AppChatSettingsScreen.Hide(this.Part(Row, "info"), Row.Why.length === 0);
            this.PaintControl(Row);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.PaintRow");
        }
    };

    /** One named piece of a row (`AppChatSettingsView.Id`). */
    private Part = (Row: AppChatSettingRow, Name: string): HTMLElement | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            return this.El(AppChatSettingsView.Id(Row.Setting, Name));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.Part");
            return null;
        }
    };

    /** The switch's state, or the pressed choice; not drawn at all before the first answer. */
    private PaintControl = (Row: AppChatSettingRow): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Off: string = String(Row.Disabled === true);
            if (AppChatSettingsRules.IsAction(Row.Setting) === true) {
                const Ask: HTMLElement | null = this.El(AppChatSettingsView.Id(Row.Setting, "ask"));
                AppChatSettingsScreen.Attr(Ask, "aria-disabled", Off);
                AppChatSettingsScreen.Hide(Ask, Row.Loading === true);
                AppChatSettingsScreen.Attr(this.El(AppChatSettingsView.Id(Row.Setting, "go")), "aria-disabled", Off);
                if (Row.Disabled === true) { AppChatSettingsScreen.Hide(this.El(AppChatSettingsView.Id(Row.Setting, "confirm")), true); }
                return;
            }
            if (AppChatSettingsRules.IsSwitch(Row.Setting) === true) {
                const Switch: HTMLElement | null = this.El(AppChatSettingsView.Id(Row.Setting, "switch"));
                AppChatSettingsScreen.Attr(Switch, "aria-checked", String(Row.On === true));
                AppChatSettingsScreen.Attr(Switch, "aria-disabled", Off);
                AppChatSettingsScreen.Hide(Switch, Row.Loading === true);
                return;
            }
            const Seg: HTMLElement | null = this.El(AppChatSettingsView.Id(Row.Setting, "seg"));
            if (Seg === null) { return; }
            const Options: NodeListOf<HTMLElement> = Seg.querySelectorAll<HTMLElement>("[" + AppChatSettingsView.ValueAttr + "]");
            for (let At: number = 0; At < Options.length; At = At + 1) {
                AppChatSettingsScreen.Attr(Options[At], "aria-pressed", String(Options[At].getAttribute(AppChatSettingsView.ValueAttr) === Row.Choice));
                AppChatSettingsScreen.Attr(Options[At], "aria-disabled", Off);
            }
            AppChatSettingsScreen.Hide(Seg, Row.Loading === true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.PaintControl");
        }
    };

    /** ⛔ Written only when the words differ (a live region rewritten with the same words is read out again). */
    private static Text = (El: HTMLElement | null, Words: string): void => {
        AppLiveText.Set(El, Words, "AppChatSettingsScreen.Text");
    };

    private static Hide = (El: HTMLElement | null, Hidden: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (El !== null && El.hidden !== Hidden) { El.hidden = Hidden; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.Hide");
        }
    };

    /** An attribute set to `Value`, or removed when `Value` is "". */
    private static Attr = (El: HTMLElement | null, Name: string, Value: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (El === null) { return; }
            if (Value.length === 0) { if (El.hasAttribute(Name) === true) { El.removeAttribute(Name); } return; }
            if (El.getAttribute(Name) !== Value) { El.setAttribute(Name, Value); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsScreen.Attr");
        }
    };
}

export class AppChatSettingsDialog implements IAppTrChipHost {
    private Host: IAppChatSettingsHost;
    private Modal: kNativeDialog | null = null;
    private Root: HTMLDivElement | null = null;
    private Selected: AppContact | null = null;
    private Run: AppChatSettingsRun | null = null;
    private Chip: AppTranslateChip | null = null;
    private Screen: AppChatSettingsScreen | null = null;
    /** The section the opener asked for, until it has been shown once. */
    private Section: string = "";

    public constructor(Host: IAppChatSettingsHost) {
        this.Host = Host;
    }

    /** Open for one chat. `Section` = `AppChatSettingsView.TrSection` to land on Translation; `Opener` gets the focus back. */
    public Open = (Selected: AppContact, Section: string = "", Opener: HTMLElement | null = null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Close();
            this.Selected = Object.assign(new AppContact(), Selected);
            this.Section = nz(Section);
            let Settings: string[] = AppChatSettingsRules.Direct;
            if (Selected.IsGroup === true) { Settings = [AppChatSettingsRules.Star]; }
            const Translator: AppTranslator | null = this.Host.TranslatorFor(this.Selected);
            const Root: HTMLDivElement = document.createElement("div");
            Root.innerHTML = AppChatSettingsView.Body(Selected.Title, Settings, Translator !== null);
            Root.addEventListener("click", this.OnClick);
            Root.addEventListener("keydown", this.OnTabKey);
            const Options: IkNativeDialogOptions = { ClassName: AppChatSettingsView.DialogClass, OnClose: this.OnClosed, ResolveReturnFocus: this.ReturnFocus };
            if (Opener !== null) { Options.ReturnFocusTo = Opener; }
            this.Root = Root;
            this.Modal = kNativeDialog.Open("Chat settings: " + Selected.Title, Root, Options);
            if (this.Modal === null) { this.OnClosed(); return; }
            this.Screen = new AppChatSettingsScreen(Root);
            this.Run = new AppChatSettingsRun(this.Ask, new AppChatSettingsPort(), this.Screen, new AppLiveWindowClock(), Settings, this.OnSettingChanged);
            this.Run.Start();
            if (Translator === null) { return; }
            this.WatchTranslation(Root);
            this.Chip = new AppTranslateChip(Translator, new AppTrDomScreen("", AppChatSettingsView.TrLineId, AppChatSettingsView.TrPanelId,
                AppChatSettingsView.TrId), this, Selected.Href, Selected.Title, true);
            this.Chip.Start();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.Open");
        }
    };

    /** Is the dialog open (for `ChatId`, or for any chat when "")? */
    public IsOpen = (ChatId: string = ""): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (this.Modal === null || this.Selected === null) { return false; }
            return ChatId.length === 0 || this.Selected.ChatId === ChatId;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.IsOpen");
            return false;
        }
    };

    /** The translation controller while the dialog is open (another tab's save makes it read again), else null. */
    public Translation = (): AppTranslateChip | null => {
        // DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
        return this.Chip;
    };

    private Ask = (): AppChatArchiveWho => {
        const Empty: AppChatArchiveWho = new AppChatArchiveWho();
        if (getCoreUtils().KErrorMode === true) { return Empty; }
        try {
            if (this.Selected === null) { return Empty; }
            return this.Host.Who(this.Selected);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.Ask");
            return Empty;
        }
    };

    /** ONE delegated listener for everything in the dialog. ⛔ A press on an `aria-disabled` control does nothing. */
    private OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Ev.target as Element | null;
            if (Target === null || typeof Target.closest !== "function") { return; }
            if (Target.closest("[" + AppChatSettingsView.CloseAttr + "]") !== null) { this.Close(); return; }
            const Tab: Element | null = Target.closest("[" + AppChatSettingsView.TabAttr + "]");
            if (Tab !== null) { this.ShowTab(nz(Tab.getAttribute(AppChatSettingsView.TabAttr)), true); return; }
            if (Target.closest("[" + AppChatSettingsView.RetryAttr + "]") !== null) { if (this.Run !== null) { this.Run.Retry(); } return; }
            const Act: Element | null = Target.closest("[" + AppTranslateView.ActAttr + "]");
            if (Act !== null) { this.OnTranslateAct(Act); return; }
            if (this.OnConfirmStep(Target) === true) { return; }
            const Control: Element | null = Target.closest("[" + AppChatSettingsView.SetAttr + "]");
            if (Control === null || this.Run === null || Control.getAttribute("aria-disabled") === "true") { return; }
            const Setting: string = nz(Control.getAttribute(AppChatSettingsView.SetAttr));
            let Text: string = nz(Control.getAttribute(AppChatSettingsView.ValueAttr));
            if (AppChatSettingsRules.IsSwitch(Setting) === true) { Text = String(Control.getAttribute("aria-checked") !== "true"); }
            const Value: AppChatSettingValue | null = AppChatSettingsRules.ValueIn(Setting, Text);
            if (Value !== null) { this.Run.Set(Setting, Value); }
            if (AppChatSettingsRules.IsAction(Setting) === true && this.Screen !== null) { this.Screen.Confirm(Setting, false); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.OnClick");
        }
    };

    /** An action's first press opens its confirm step; Cancel closes it. ⛔ Neither sends anything. */
    private OnConfirmStep = (Target: Element): boolean => {
        if (getCoreUtils().KErrorMode === true) { return true; }
        try {
            const Ask: Element | null = Target.closest("[" + AppChatSettingsView.AskAttr + "]");
            const Cancel: Element | null = Target.closest("[" + AppChatSettingsView.CancelAttr + "]");
            if (Ask === null && Cancel === null) { return false; }
            if (this.Screen === null) { return true; }
            if (Cancel !== null) { this.Screen.Confirm(nz(Cancel.getAttribute(AppChatSettingsView.CancelAttr)), false); return true; }
            if ((Ask as Element).getAttribute("aria-disabled") === "true") { return true; }
            this.Screen.Confirm(nz((Ask as Element).getAttribute(AppChatSettingsView.AskAttr)), true);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.OnConfirmStep");
            return true;
        }
    };

    /** A press in the Translation section (`data-wac-tract`): Save, Add, Remove, the suggestion's Translate / Not now. */
    private OnTranslateAct = (Control: Element): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Chip === null || Control.getAttribute("aria-disabled") === "true") { return; }
            this.Chip.Act(nz(Control.getAttribute(AppTranslateView.ActAttr)));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.OnTranslateAct");
        }
    };

    private OnSettingChanged = (Setting: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Selected !== null) { this.Host.SettingChanged(this.Selected, Setting); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.OnSettingChanged");
        }
    };

    /** `IAppTrChipHost.Changed`: the translation settings were saved from this dialog. */
    public Changed = (Href: string, TurnedOn: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Host.TranslationChanged(Href, TurnedOn);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.Changed");
        }
    };

    /** Show one tab's panel, hide the others; `Focus` puts the keyboard on the tab. Only attributes change - nothing is rebuilt. */
    private ShowTab = (Key: string, Focus: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Root: HTMLElement | null = this.Root;
            if (Root === null) { return; }
            Root.querySelectorAll("[" + AppChatSettingsView.TabAttr + "]").forEach((El: Element): void => {
                const Tab: HTMLElement = El as HTMLElement;
                const Own: string = nz(Tab.getAttribute(AppChatSettingsView.TabAttr));
                const On: boolean = Own === Key;
                if (Tab.getAttribute("aria-selected") !== String(On)) { Tab.setAttribute("aria-selected", String(On)); }
                Tab.tabIndex = On ? 0 : -1;
                const Panel: HTMLElement | null = Root.querySelector("[id=\"" + AppChatSettingsView.PanelId(Own) + "\"]");
                if (Panel !== null && Panel.hidden !== (On === false)) { Panel.hidden = On === false; }
                if (On === true && Focus === true) { Tab.focus(); }
            });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.ShowTab");
        }
    };

    /** Arrow keys / Home / End move between the visible tabs (the WAI tabs pattern, automatic activation). */
    private OnTabKey = (Ev: KeyboardEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: Element | null = Ev.target as Element | null;
            if (Target === null || typeof Target.closest !== "function" || this.Root === null) { return; }
            if (Target.closest("[" + AppChatSettingsView.TabAttr + "]") === null) { return; }
            const Keys: string[] = [];
            this.Root.querySelectorAll("[" + AppChatSettingsView.TabAttr + "]").forEach((Tab: Element): void => {
                if ((Tab as HTMLElement).hidden !== true) { Keys.push(nz(Tab.getAttribute(AppChatSettingsView.TabAttr))); }
            });
            const At: number = Keys.indexOf(nz(Target.getAttribute(AppChatSettingsView.TabAttr)));
            let Next: number = -1;
            if (Ev.key === "ArrowRight") { Next = (At + 1) % Keys.length; }
            if (Ev.key === "ArrowLeft") { Next = (At - 1 + Keys.length) % Keys.length; }
            if (Ev.key === "Home") { Next = 0; }
            if (Ev.key === "End") { Next = Keys.length - 1; }
            if (Next < 0 || Keys.length === 0) { return; }
            Ev.preventDefault();
            this.ShowTab(Keys[Next], true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.OnTabKey");
        }
    };

    /** The Translation tab is offered only while its section is (hidden until `/translate` says available). */
    private WatchTranslation = (Root: HTMLElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Box: HTMLElement | null = Root.querySelector("[id=\"" + AppChatSettingsView.TrId + "\"]");
            const Tab: HTMLElement | null = Root.querySelector("[id=\"" + AppChatSettingsView.TabId(AppChatSettingsView.TrSection) + "\"]");
            if (Box === null || Tab === null) { return; }
            const Sync = (): void => {
                if (getCoreUtils().KErrorMode === true) { return; }
                try {
                    if (Tab.hidden !== Box.hidden) { Tab.hidden = Box.hidden; }
                    if (Box.hidden === true && Tab.getAttribute("aria-selected") === "true") { this.ShowTab(AppChatSettingsView.ChatTab, false); }
                } catch (e) {
                    getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.WatchTranslation.Sync");
                }
            };
            Sync();
            new MutationObserver(Sync).observe(Box, { attributes: true, attributeFilter: ["hidden"] });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.WatchTranslation");
        }
    };

    /**
     * `IAppTrChipHost.Heard`: translation's state for this chat is known. Opened AT Translation (the header's chip): the
     * section is brought into view and the keyboard put on its first control - once. ⛔ Focus only.
     */
    public Heard = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Section !== AppChatSettingsView.TrSection || this.Root === null) { return; }
            const Box: HTMLElement | null = this.Root.querySelector("[id=\"" + AppChatSettingsView.TrId + "\"]");
            if (Box === null || Box.hidden === true) { return; }
            this.Section = "";
            this.ShowTab(AppChatSettingsView.TrSection, false);
            if (typeof Box.scrollIntoView === "function") { Box.scrollIntoView({ block: "start" }); }
            AppFocus.ToFirst(this.Root.querySelector("[id=\"" + AppChatSettingsView.TrPanelId + "\"]") as unknown as IFocusEl | null);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.Heard");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: closing only asks the page where the keyboard should go back to. */
    private ReturnFocus = (): HTMLElement | null => {
        try {
            if (this.Selected === null) { return null; }
            return this.Host.Opener(this.Selected);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.ReturnFocus");
            return null;
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: disposal must stop the reads and timers even after the global error gate closed. */
    private OnClosed = (): void => {
        try {
            if (this.Run !== null) { this.Run.Stop(); }
            if (this.Chip !== null) { this.Chip.Stop(); }
            if (this.Root !== null) { this.Root.removeEventListener("click", this.OnClick); }
            this.Run = null; this.Chip = null; this.Screen = null; this.Root = null; this.Selected = null; this.Modal = null; this.Section = "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.OnClosed");
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: an open layer can always be dismissed and its reads stopped after a fault. */
    public Close = (): void => {
        try {
            const Modal: kNativeDialog | null = this.Modal;
            if (Modal !== null) { Modal.Close(); }
            this.OnClosed();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatSettingsDialog.Close");
        }
    };
}
